import { meanScore } from './filters';
import type { YougovMonthlyRow } from './yougovData.schema';

export type FunnelStage = 'awareness' | 'consideration' | 'purchaseIntent';

export const FUNNEL_STAGE_ORDER: FunnelStage[] = ['awareness', 'consideration', 'purchaseIntent'];

/* eslint-disable i18next/no-literal-string -- YouGov wire metric keys, not copy */
/** YouGov metric keys backing each funnel stage. Wire values, never translated. */
export const METRIC_BY_STAGE: Record<FunnelStage, string> = {
  awareness: 'aided',
  consideration: 'consider',
  purchaseIntent: 'likelybuy',
};
/* eslint-enable i18next/no-literal-string */

export type FunnelStageValues = Record<FunnelStage, number | null>;

export interface FunnelMatrix {
  brands: string[];
  targetBrand: string;
  values: Record<string, FunnelStageValues>;
  competitorAverage: FunnelStageValues;
  /**
   * Drop-off between each pair of consecutive stages for the competitor average,
   * indexed like `FUNNEL_STAGE_ORDER` (so index 0 is awareness→consideration).
   * Null whenever the two stages' averages were not drawn from the same set of
   * competitors — a fabricated cross-cohort percentage is worse than a dash.
   */
  competitorAverageDropoffs: (number | null)[];
}

/**
 * Target always leads, even when it is missing from `availableBrands` (the backend
 * can persist exactly that: `pickTargetBrand` falls back to the analysis's brand
 * name when no row carries a scored funnel metric). Downstream code then always
 * has a target column to render — with no rows it shows em-dashes, the honest
 * "your brand, no data" result, rather than the target silently vanishing.
 */
export function orderBrands(availableBrands: string[], targetBrand: string): string[] {
  const rest = availableBrands.filter((b) => b !== targetBrand);
  return [targetBrand, ...rest];
}

/**
 * Brands with at least one scored funnel metric (`aided`, `consider`, `likelybuy`) in
 * the given rows — the backend's own funnel cohort. `availableBrands` is every distinct
 * brand label in the workbook, which over-includes brands with only extended-metric
 * data; passing that whole list to `computeFunnelMatrix` gives those brands a full
 * column of dashes and starves every column's share of the grid's width. The target
 * brand is always kept even with no funnel data, so `orderBrands` still gives it a
 * column (rendering dashes, the honest result) rather than dropping it.
 */
export function funnelCohort(
  rows: YougovMonthlyRow[],
  availableBrands: string[],
  targetBrand: string,
): string[] {
  const funnelMetrics = new Set(Object.values(METRIC_BY_STAGE));
  const scored = new Set(
    rows.filter((r) => funnelMetrics.has(r.metric) && r.score !== null).map((r) => r.brand),
  );
  return availableBrands.filter((brand) => brand === targetBrand || scored.has(brand));
}

export function stageValue(
  rows: YougovMonthlyRow[],
  brand: string,
  stage: FunnelStage,
): number | null {
  const metric = METRIC_BY_STAGE[stage];
  return meanScore(rows.filter((r) => r.brand === brand && r.metric === metric));
}

/**
 * Percentage of the previous stage lost by this one. Mirrors the backend's
 * `dropoffsFor` so both sides agree if ever shown together. A zero or missing
 * previous stage has no meaningful drop-off, so it yields null rather than a
 * misleading 0% or Infinity.
 */
export function dropoff(prev: number | null, curr: number | null): number | null {
  if (prev === null || curr === null || prev === 0) return null;
  return ((prev - curr) / prev) * 100;
}

export function emptyStages(): FunnelStageValues {
  return { awareness: null, consideration: null, purchaseIntent: null };
}

export function computeFunnelMatrix(
  rows: YougovMonthlyRow[],
  targetBrand: string,
  availableBrands: string[],
): FunnelMatrix {
  const brands = orderBrands(availableBrands, targetBrand);
  const values: FunnelMatrix['values'] = {};
  for (const brand of brands) {
    const stages = emptyStages();
    for (const stage of FUNNEL_STAGE_ORDER) stages[stage] = stageValue(rows, brand, stage);
    values[brand] = stages;
  }

  const competitors = brands.filter((b) => b !== targetBrand);
  const competitorAverage = emptyStages();
  const contributorsByStage: Record<FunnelStage, Set<string>> = {
    awareness: new Set(),
    consideration: new Set(),
    purchaseIntent: new Set(),
  };
  for (const stage of FUNNEL_STAGE_ORDER) {
    const contributors = competitors.filter((b) => values[b][stage] !== null);
    contributorsByStage[stage] = new Set(contributors);
    const measured = contributors.map((b) => values[b][stage] as number);
    // Null, not 0: the backend uses 0 here and then needs a `> 0` special case to
    // tell "no competitor data" apart from a genuine zero. Keep that ambiguity out.
    competitorAverage[stage] =
      measured.length > 0 ? measured.reduce((a, b) => a + b, 0) / measured.length : null;
  }

  const competitorAverageDropoffs = FUNNEL_STAGE_ORDER.slice(0, -1).map((stage, i) => {
    const nextStage = FUNNEL_STAGE_ORDER[i + 1];
    const sameCohort = setsEqual(contributorsByStage[stage], contributorsByStage[nextStage]);
    return sameCohort ? dropoff(competitorAverage[stage], competitorAverage[nextStage]) : null;
  });

  return { brands, targetBrand, values, competitorAverage, competitorAverageDropoffs };
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every((value) => b.has(value));
}
